import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { MAIN_URL } from "../data/const";

// Default configuration that can be overridden
const DEFAULT_CONFIG = {
  baseURL: `${MAIN_URL}/api/v1/himraahi`,
  ownerBaseURL: `${MAIN_URL}/api/v1/owner`,
  vehicleBaseURL: `${MAIN_URL}/api/v1/vehicle`,
  authTokenKey: "token",
  userKey: "user",
  endpoints: {
    login: "/owner",
    logout: "/logout",
  },
  tokenPrefix: "Bearer",
};

// Create API instance with default config
const createAPI = (baseURL, withAuth = true) => {
  return axios.create({
    baseURL,
    withCredentials: true,
    headers: withAuth
      ? {
          "Content-Type": "application/json",
        }
      : {},
  });
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children, config = {} }) => {
  const apiConfig = { ...DEFAULT_CONFIG, ...config };
  const [token, setToken] = useState(
    localStorage.getItem(apiConfig.authTokenKey)
  );
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Create main API instance for authentication
  const [api] = useState(() => createAPI(apiConfig.baseURL));

  // Create secondary API instance specifically for owner operations (Team page, etc.)
  const [ownerApi] = useState(() => createAPI(apiConfig.ownerBaseURL));

  // Create secondary API instance specifically for owner operations (Vehicle page, etc.)
  const [vehicleApi] = useState(() => createAPI(apiConfig.vehicleBaseURL));

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem(apiConfig.authTokenKey);
      if (storedToken) {
        try {
          // Apply token to both API instances
          const authHeader = `${apiConfig.tokenPrefix} ${storedToken}`;
          api.defaults.headers.common["Authorization"] = authHeader;
          ownerApi.defaults.headers.common["Authorization"] = authHeader;
          vehicleApi.defaults.headers.common["Authorization"] = authHeader;
          const storedUser = localStorage.getItem(apiConfig.userKey);
          if (storedUser) {
            setUser(JSON.parse(storedUser));
          }

          setToken(storedToken);
        } catch (error) {
          console.error("Auth initialization error:", error);
          handleLogout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [
    api,
    ownerApi,
    vehicleApi,
    apiConfig.authTokenKey,
    apiConfig.userKey,
    apiConfig.tokenPrefix,
  ]);

  const handleLogin = async (credentials) => {
    try {
      // console.log("auth", apiConfig.endpoints.login, credentials);
      const response = await api.post(apiConfig.endpoints.login, credentials);

      const { data } = response;

      if (!data.success) {
        throw new Error(data.message || "Login failed");
      }

      // Store authentication data
      const authToken = data.data.token;
      const userData = data.data.owner;

      localStorage.setItem(apiConfig.authTokenKey, authToken);
      localStorage.setItem(apiConfig.userKey, JSON.stringify(userData));

      // Update state
      setToken(authToken);
      setUser(userData);

      // Set default auth header for both API instances
      const authHeader = `${apiConfig.tokenPrefix} ${authToken}`;
      api.defaults.headers.common["Authorization"] = authHeader;
      ownerApi.defaults.headers.common["Authorization"] = authHeader;
      vehicleApi.defaults.headers.common["Authorization"] = authHeader;
      return data;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      if (apiConfig.endpoints.logout) {
        await api.post(apiConfig.endpoints.logout);
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear storage
      localStorage.removeItem(apiConfig.authTokenKey);
      localStorage.removeItem(apiConfig.userKey);

      // Reset state
      setToken(null);
      setUser(null);

      // Clear auth header from both API instances
      delete api.defaults.headers.common["Authorization"];
      delete ownerApi.defaults.headers.common["Authorization"];
      delete vehicleApi.defaults.headers.common["Authorization"];
    }
  };

  // Add interceptors to both API instances
  useEffect(() => {
    const mainInterceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          handleLogout();
        }
        return Promise.reject(error);
      }
    );

    const ownerInterceptor = ownerApi.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          handleLogout();
        }
        return Promise.reject(error);
      }
    );

    const vehicleInterceptor = vehicleApi.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          handleLogout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(mainInterceptor);
      ownerApi.interceptors.response.eject(ownerInterceptor);
      vehicleApi.interceptors.response.eject(vehicleInterceptor);
    };
  }, [api, ownerApi, vehicleApi]);

  const contextValue = {
    token,
    user,
    loading,
    login: handleLogin,
    logout: handleLogout,
    api,
    ownerApi,
    vehicleApi,
    isAuthenticated: !!token,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default useAuth;
